import React, { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { useLang } from "../i18n";
import { apiError } from "../lib/api";
import { Skull } from "lucide-react";

const HERO = "https://images.pexels.com/photos/20993079/pexels-photo-20993079.jpeg";

export default function Register() {
  const { register, googleLogin } = useAuth();
  const { t, lang, toggle } = useLang();
  const navigate = useNavigate();
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError(""); setLoading(true);
    try {
      await register(email, password, name);
      navigate("/dashboard");
    } catch (err) {
      setError(apiError(err.response?.data?.detail) || err.message);
    } finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-background">
      <div className="hidden lg:block relative">
        <img src={HERO} alt="concert" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-black/80" />
        <div className="relative h-full flex flex-col justify-end p-12">
          <Skull className="text-primary mb-4" size={48} />
          <h1 className="heading text-5xl font-bold text-white">MetalVault</h1>
          <p className="text-zinc-300 mt-3 text-lg max-w-md">{t("tagline")}</p>
        </div>
      </div>

      <div className="flex items-center justify-center p-6 pb-28 sm:pb-6">
        <div className="w-full max-w-sm animate-fade-up">
          <div className="flex justify-end mb-8">
            <button onClick={toggle} className="text-xs label-caps text-zinc-400 hover:text-white" data-testid="lang-toggle">{lang.toUpperCase()}</button>
          </div>
          <h2 className="heading text-3xl font-bold text-white mb-6">{t("register")}</h2>

          {error && <div data-testid="register-error" className="mb-4 text-sm text-red-400 border border-red-900 bg-red-950/40 px-3 py-2 rounded-sm">{error}</div>}

          <form onSubmit={submit} className="space-y-4">
            <div>
              <label className="label-caps text-xs text-zinc-400">{t("name")}</label>
              <input data-testid="register-name" value={name} onChange={(e) => setName(e.target.value)}
                className="mt-1 w-full bg-black border border-zinc-800 rounded-sm px-3 py-2.5 text-white focus:border-primary focus:outline-none" />
            </div>
            <div>
              <label className="label-caps text-xs text-zinc-400">{t("email")}</label>
              <input data-testid="register-email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)}
                className="mt-1 w-full bg-black border border-zinc-800 rounded-sm px-3 py-2.5 text-white focus:border-primary focus:outline-none" />
            </div>
            <div>
              <label className="label-caps text-xs text-zinc-400">{t("password")}</label>
              <input data-testid="register-password" type="password" required value={password} onChange={(e) => setPassword(e.target.value)}
                className="mt-1 w-full bg-black border border-zinc-800 rounded-sm px-3 py-2.5 text-white focus:border-primary focus:outline-none" />
            </div>
            <button data-testid="register-submit" disabled={loading} type="submit"
              className="w-full bg-primary text-primary-foreground label-caps py-3 rounded-sm font-semibold hover:bg-yellow-400 transition-colors disabled:opacity-60">
              {loading ? "..." : t("register")}
            </button>
          </form>

          <div className="my-6 flex items-center gap-3 text-zinc-600 text-xs label-caps">
            <div className="flex-1 h-px bg-zinc-800" /> OR <div className="flex-1 h-px bg-zinc-800" />
          </div>

          <button data-testid="google-login" onClick={googleLogin}
            className="w-full border border-zinc-700 text-white py-3 rounded-sm hover:bg-zinc-900 transition-colors flex items-center justify-center gap-2">
            <img src="https://www.svgrepo.com/show/475656/google-color.svg" alt="g" className="w-5 h-5" />
            {t("google_login")}
          </button>

          <p className="mt-6 text-sm text-zinc-400">
            {t("have_account")}{" "}
            <Link to="/login" data-testid="go-login" className="text-primary hover:underline">{t("login")}</Link>
          </p>
        </div>
      </div>
    </div>
  );
}
